import { getRouteApi, Link, useLocation } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/new-ui/breadcrumb';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/new-ui/tooltip';
import { useEstate } from '@/features/estates-property/api/estate';
import { useProject } from '@/features/projects/api/projects';
import { TopRightFunction } from './top-right-function';

const estateRoute = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout'
);
const projectRoute = getRouteApi(
  '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout'
);

// Same pattern the dashboard layout uses to hide the tabs
const editorRoutePattern = /^\/(estates|projects)\/[^/]+\/media\/edit\/[^/]+$/;

type Crumb = {
  key: string;
  label: string;
  node: React.ReactNode;
};

type HeaderViewProps = {
  crumbs: Crumb[];
  title: string;
  isEditor: boolean;
  /** Shown before the title, e.g. the project icon */
  icon?: React.ReactNode;
  t: (key: string) => string;
};

const HeaderView = ({ crumbs, title, isEditor, icon, t }: HeaderViewProps) => (
  <div className="space-y-2">
    <Breadcrumb aria-label={t('breadcrumbNavLabel')}>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => (
          <Fragment key={crumb.key}>
            <BreadcrumbItem className="min-w-0">{crumb.node}</BreadcrumbItem>
            {index < crumbs.length - 1 && <BreadcrumbSeparator />}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <h1 className="flex min-w-0 items-center gap-2 break-words text-2xl font-semibold tracking-tight text-foreground">
        {!isEditor && icon}
        <span className="min-w-0">{isEditor ? t('editImage') : title}</span>
      </h1>
      <TopRightFunction />
    </div>
  </div>
);

const EstateHeader = () => {
  const { t } = useTranslation();
  const { estateId } = estateRoute.useParams();
  const { data: estate } = useEstate({ id: estateId });
  const { pathname } = useLocation();
  const isEditor = editorRoutePattern.test(pathname);
  const name = estate?.name || t('unnamedEstate');

  const crumbs: Crumb[] = [
    {
      key: 'estates',
      label: t('estates'),
      node: (
        <BreadcrumbLink asChild>
          <Link className="inline-flex items-center gap-1.5" to="/estates">
            {t('estates')}
          </Link>
        </BreadcrumbLink>
      ),
    },
  ];
  if (estate?.projects?.id) {
    const project = estate.projects;
    const label = project.name || t('unnamedProject');
    crumbs.push({
      key: 'project',
      label,
      node: (
        <BreadcrumbLink asChild>
          <Link
            className="inline-flex items-center gap-1.5"
            to="/projects/$projectId"
            params={{ projectId: project.id }}
          >
            <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {label}
          </Link>
        </BreadcrumbLink>
      ),
    });
  }
  if (isEditor) {
    crumbs.push(
      {
        key: 'estate',
        label: name,
        node: (
          <BreadcrumbLink asChild>
            <Link
              className="inline-flex items-center gap-1.5"
              to="/estates/$estateId"
              params={{ estateId }}
            >
              {name}
            </Link>
          </BreadcrumbLink>
        ),
      },
      {
        key: 'media',
        label: t('media'),
        node: (
          <BreadcrumbLink asChild>
            <Link
              className="inline-flex items-center gap-1.5"
              to="/estates/$estateId/media"
              params={{ estateId }}
            >
              {t('media')}
            </Link>
          </BreadcrumbLink>
        ),
      },
      {
        key: 'images',
        label: t('images'),
        node: (
          <BreadcrumbLink asChild>
            <Link
              className="inline-flex items-center gap-1.5"
              to="/estates/$estateId/media/images"
              params={{ estateId }}
            >
              {t('images')}
            </Link>
          </BreadcrumbLink>
        ),
      }
    );
  }

  return <HeaderView crumbs={crumbs} title={name} isEditor={isEditor} t={t} />;
};

const ProjectHeader = () => {
  const { t } = useTranslation();
  const { projectId } = projectRoute.useParams();
  const { data: project } = useProject({ id: projectId });
  const { pathname } = useLocation();
  const isEditor = editorRoutePattern.test(pathname);
  const name = project?.name || t('unnamedProject');

  const crumbs: Crumb[] = [
    {
      key: 'estates',
      label: t('estates'),
      node: (
        <BreadcrumbLink asChild>
          <Link className="inline-flex items-center gap-1.5" to="/estates">
            {t('estates')}
          </Link>
        </BreadcrumbLink>
      ),
    },
  ];
  if (isEditor) {
    crumbs.push(
      {
        key: 'project',
        label: name,
        node: (
          <BreadcrumbLink asChild>
            <Link
              className="inline-flex items-center gap-1.5"
              to="/projects/$projectId"
              params={{ projectId }}
            >
              {name}
            </Link>
          </BreadcrumbLink>
        ),
      },
      {
        key: 'media',
        label: t('media'),
        node: (
          <BreadcrumbLink asChild>
            <Link
              className="inline-flex items-center gap-1.5"
              to="/projects/$projectId/media"
              params={{ projectId }}
            >
              {t('media')}
            </Link>
          </BreadcrumbLink>
        ),
      },
      {
        key: 'images',
        label: t('images'),
        node: (
          <BreadcrumbLink asChild>
            <Link
              className="inline-flex items-center gap-1.5"
              to="/projects/$projectId/media/images"
              params={{ projectId }}
            >
              {t('images')}
            </Link>
          </BreadcrumbLink>
        ),
      }
    );
  }

  return (
    <HeaderView
      crumbs={crumbs}
      title={name}
      isEditor={isEditor}
      icon={
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Focusable, so keyboard users get the tooltip too */}
            {/* biome-ignore lint/a11y/noNoninteractiveTabindex: tooltip trigger */}
            <span className="inline-flex shrink-0 rounded-sm" tabIndex={0}>
              <Building2 className="h-6 w-6 text-muted-foreground" aria-label={t('project')} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('project')}</TooltipContent>
        </Tooltip>
      }
      t={t}
    />
  );
};

export const EntityPageHeader = ({ type }: { type: 'estate' | 'project' }) =>
  type === 'estate' ? <EstateHeader /> : <ProjectHeader />;
